import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { User, Lock, Eye, EyeOff, Stethoscope, Copy, Check, Shield, Smartphone, AlertTriangle, Clock, UserPlus, Key, Activity, Bell, LogOut } from 'lucide-react';

// Import components
import PasswordRecoveryModal from './components/PasswordRecoveryModal';
import MfaSetupModal from './components/MfaSetupModal';
import SessionManagementPanel from './components/SessionManagementPanel';
import UserAdministrationPanel from './components/UserAdministrationPanel';
import SystemNotifications from './components/SystemNotifications';
import SecurityMonitoring from './components/SecurityMonitoring';

const AuthenticationPortal = () => {
  const [activeTab, setActiveTab] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPasswordRecovery, setShowPasswordRecovery] = useState(false);
  const [showMfaSetup, setShowMfaSetup] = useState(false);
  const [copiedCredential, setCopiedCredential] = useState('');
  const [sessionWarning, setSessionWarning] = useState(null);
  const { signIn, signInWithGoogle, signOut, user, userProfile, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Demo credentials for easy testing
  const demoCredentials = [
    { role: 'Admin', email: 'admin@kmchospital.com', password: 'm8Qx2pL9w', color: 'bg-red-500' },
    { role: 'Doctor', email: 'doctor@kmchospital.com', password: 'k7Nv4hR3t', color: 'bg-blue-500' },
    { role: 'Nurse', email: 'nurse@kmchospital.com', password: 'j6Bm1xT8q', color: 'bg-green-500' },
    { role: 'Lab Tech', email: 'labtech@kmchospital.com', password: 'r5Hc9wP2n', color: 'bg-purple-500' },
    { role: 'Pharmacist', email: 'pharmacist@kmchospital.com', password: 's3Yf7zK4m', color: 'bg-orange-500' },
    { role: 'Accountant', email: 'accountant@kmchospital.com', password: 'kobokobo11', color: 'bg-teal-500' }
  ];

  // Role-based dashboard navigation
  const getRoleBasedDashboard = (role) => {
    const dashboards = {
      'admin': '/user-administration-dashboard',
      'doctor': '/doctor-dashboard',
      'nurse': '/nursing-dashboard',
      'lab_technician': '/laboratory-dashboard',
      'pharmacist': '/pharmacy-dashboard',
      'receptionist': '/patient-registration',
      'billing_clerk': '/accounts-dashboard',
      'accountant': '/accounts-dashboard',
      'finance_manager': '/accounts-dashboard',
      'auditor': '/accounts-dashboard'
    };
    return dashboards?.[role] || '/doctor-dashboard';
  };

  // Session timeout warning
  useEffect(() => {
    if (user && activeTab === 'login') {
      const timeoutWarning = setTimeout(() => {
        setSessionWarning({
          message: 'Your session will expire in 5 minutes. Do you want to extend it?',
          countdown: 300 // 5 minutes in seconds
        });
      }, 25 * 60 * 1000); // Show warning 5 minutes before 30-minute timeout

      return () => clearTimeout(timeoutWarning);
    }
  }, [user, activeTab]);

  // Redirect authenticated users
  useEffect(() => {
    if (user && !authLoading && userProfile) {
      const targetDashboard = getRoleBasedDashboard(userProfile?.role);
      navigate(targetDashboard);
    }
  }, [user, authLoading, userProfile, navigate]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { error: signInError } = await signIn(email, password);

      if (signInError) {
        if (signInError?.message?.includes('Invalid login credentials')) {
          setError('Invalid email or password. Please check your credentials and try again.');
        } else if (signInError?.message?.includes('Failed to fetch') ||
          signInError?.message?.includes('AuthRetryableFetchError')) {
          setError('Cannot connect to authentication service. Please check your authentication provider (Firebase) and your network connection.');
        } else if (signInError?.message?.includes('Email not confirmed')) {
          setError('Please check your email and confirm your account before signing in.');
        } else {
          setError(signInError?.message || 'Login failed. Please try again.');
        }
      } else {
        // Check if MFA is required (mock for demonstration)
        const userNeedsMFA = email?.includes('admin'); // Mock logic
        if (userNeedsMFA) {
          setMfaRequired(true);
          setError('');
        }
      }
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') ||
        error?.message?.includes('NetworkError') ||
        error?.name === 'TypeError' && error?.message?.includes('fetch')) {
        setError('Cannot connect to authentication service. Please check your internet connection or contact support.');
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMfaVerification = async (e) => {
    e?.preventDefault();
    if (!mfaCode) {
      setError('Please enter the MFA code');
      return;
    }

    setLoading(true);
    // Mock MFA verification - in real implementation, verify with backend
    setTimeout(() => {
      if (mfaCode === '123456' || mfaCode?.length === 6) {
        setMfaRequired(false);
        setError('');
        setActiveTab('authenticated');
      } else {
        setError('Invalid MFA code. Please try again.');
      }
      setLoading(false);
    }, 1000);
  };

  const handleSignOut = async () => {
    setLoading(true);
    try {
      await signOut();
      setActiveTab('login');
      setEmail('');
      setPassword('');
      setMfaCode('');
      setError('');
      setMfaRequired(false);
    } catch (error) {
      setError('Sign out failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const extendSession = () => {
    setSessionWarning(null);
    // In real implementation, extend session with backend
  };

  const copyCredential = async (credential) => {
    try {
      await navigator?.clipboard?.writeText(`${credential?.email} / ${credential?.password}`);
      setCopiedCredential(credential?.email);
      setTimeout(() => setCopiedCredential(''), 2000);
    } catch (error) {
      setEmail(credential?.email);
      setPassword(credential?.password);
    }
  };

  const fillCredential = (credential) => {
    setEmail(credential?.email);
    setPassword(credential?.password);
    setError('');
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // If user is authenticated, show admin portal
  if (user && userProfile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="container mx-auto px-4 py-8">
          {/* Header */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-blue-100 rounded-full">
                  <Stethoscope className="h-8 w-8 text-blue-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Authentication Portal</h1>
                  <p className="text-gray-600">Welcome back, {userProfile?.full_name}</p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                  {userProfile?.role?.toUpperCase()}
                </span>
                <button
                  onClick={handleSignOut}
                  className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  <LogOut className="h-4 w-4" />
                  <span>Sign Out</span>
                </button>
              </div>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="bg-white rounded-xl shadow-lg mb-8">
            <div className="border-b border-gray-200">
              <nav className="flex space-x-8 px-6">
                {['sessions', 'notifications', 'security', userProfile?.role === 'admin' && 'administration']?.filter(Boolean)?.map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`py-4 px-2 border-b-2 font-medium text-sm ${activeTab === tab
                      ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                  >
                    <div className="flex items-center space-x-2">
                      {tab === 'sessions' && <Activity className="h-4 w-4" />}
                      {tab === 'notifications' && <Bell className="h-4 w-4" />}
                      {tab === 'security' && <Shield className="h-4 w-4" />}
                      {tab === 'administration' && <UserPlus className="h-4 w-4" />}
                      <span className="capitalize">{tab === 'administration' ? 'User Administration' : tab}</span>
                    </div>
                  </button>
                ))}
              </nav>
            </div>

            {/* Tab Content */}
            <div className="p-6">
              {activeTab === 'sessions' && <SessionManagementPanel />}
              {activeTab === 'notifications' && <SystemNotifications />}
              {activeTab === 'security' && <SecurityMonitoring />}
              {activeTab === 'administration' && userProfile?.role === 'admin' && <UserAdministrationPanel />}
            </div>
          </div>
        </div>
        {/* Session Warning Modal */}
        {sessionWarning && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <div className="flex items-center space-x-3 mb-4">
                <AlertTriangle className="h-6 w-6 text-orange-500" />
                <h3 className="text-lg font-semibold">Session Timeout Warning</h3>
              </div>
              <p className="text-gray-600 mb-6">{sessionWarning?.message}</p>
              <div className="flex space-x-3">
                <button
                  onClick={extendSession}
                  className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Extend Session
                </button>
                <button
                  onClick={handleSignOut}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Sign Out
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="max-w-6xl w-full flex bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Left Panel - Authentication Form */}
        <div className="w-full lg:w-1/2 p-8">
          <div className="max-w-md mx-auto">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="flex justify-center mb-4">
                <div className="p-3 bg-blue-100 rounded-full">
                  <Stethoscope className="h-8 w-8 text-blue-600" />
                </div>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">KMC Hospital</h1>
              <p className="text-gray-600">Healthcare Management System - Authentication Portal</p>
            </div>

            {/* Login Form */}
            {!mfaRequired ? (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Email Field */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e?.target?.value)}
                      className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="Enter your email"
                      disabled={loading}
                    />
                  </div>
                </div>

                {/* Password Field */}
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e?.target?.value)}
                      className="block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="Enter your password"
                      disabled={loading}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={loading}
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                      ) : (
                        <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Remember Me & Forgot Password */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <input
                      id="remember-me"
                      name="remember-me"
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e?.target?.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900">
                      Remember me
                    </label>
                  </div>
                  <div className="text-sm">
                    <button
                      type="button"
                      onClick={() => setShowPasswordRecovery(true)}
                      className="font-medium text-blue-600 hover:text-blue-500"
                    >
                      Forgot password?
                    </button>
                  </div>
                </div>

                {/* Error Message */}
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <AlertTriangle className="h-5 w-5 text-red-400" />
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-red-800">{error}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading || !email || !password}
                  className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Signing in...
                    </>
                  ) : (
                    'Sign In'
                  )}
                </button>

                <div className="relative mt-6 mb-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white text-gray-500">Or continue with</span>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={async () => {
                    setLoading(true);
                    setError('');
                    const { error: googleError } = await signInWithGoogle();
                    if (googleError) {
                      setError(googleError.message);
                    }
                    setLoading(false);
                  }}
                  disabled={loading}
                  className="w-full flex justify-center items-center py-3 px-4 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  Sign in with Google
                </button>
              </form>
            ) : (
              /* MFA Form */
              (<form onSubmit={handleMfaVerification} className="space-y-6">
                <div className="text-center">
                  <div className="flex justify-center mb-4">
                    <div className="p-3 bg-green-100 rounded-full">
                      <Shield className="h-8 w-8 text-green-600" />
                    </div>
                  </div>
                  <h2 className="text-xl font-bold text-gray-900 mb-2">Multi-Factor Authentication</h2>
                  <p className="text-gray-600">Enter the code from your authenticator app</p>
                </div>
                <div>
                  <label htmlFor="mfa-code" className="block text-sm font-medium text-gray-700 mb-2">
                    Authentication Code
                  </label>
                  <input
                    id="mfa-code"
                    type="text"
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e?.target?.value)}
                    className="block w-full py-3 px-4 border border-gray-300 rounded-lg text-center text-lg font-mono tracking-widest focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="000000"
                    maxLength="6"
                    disabled={loading}
                  />
                </div>
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex">
                      <AlertTriangle className="h-5 w-5 text-red-400" />
                      <div className="ml-3">
                        <p className="text-sm text-red-800">{error}</p>
                      </div>
                    </div>
                  </div>
                )}
                <button
                  type="submit"
                  disabled={loading || mfaCode?.length !== 6}
                  className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Verifying...
                    </>
                  ) : (
                    'Verify Code'
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setMfaRequired(false)}
                  className="w-full text-center text-sm text-gray-600 hover:text-gray-800"
                >
                  Back to login
                </button>
              </form>)
            )}

            {/* Security Features */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <div className="flex items-center justify-center space-x-6 text-sm text-gray-500">
                <div className="flex items-center space-x-1">
                  <Shield className="h-4 w-4" />
                  <span>Secure Login</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Smartphone className="h-4 w-4" />
                  <span>MFA Support</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Key className="h-4 w-4" />
                  <span>Password Recovery</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Panel - Demo Credentials & Features */}
        <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-blue-600 to-indigo-700 p-8 text-white">
          <div className="flex flex-col justify-center max-w-md mx-auto">
            <h2 className="text-2xl font-bold mb-6">Demo Credentials</h2>
            <p className="text-blue-100 mb-6">Use these test accounts to explore different role-based access levels:</p>

            <div className="space-y-3 mb-8">
              {demoCredentials?.map((credential, index) => (
                <div key={index} className="bg-white/10 backdrop-blur rounded-lg p-4 border border-white/20">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center">
                      <div className={`w-3 h-3 ${credential?.color} rounded-full mr-2`}></div>
                      <span className="font-semibold">{credential?.role}</span>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => copyCredential(credential)}
                        className="p-1 hover:bg-white/20 rounded transition-colors"
                        title="Copy credentials"
                      >
                        {copiedCredential === credential?.email ? (
                          <Check className="h-4 w-4 text-green-300" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </button>
                      <button
                        onClick={() => fillCredential(credential)}
                        className="px-3 py-1 bg-white/20 hover:bg-white/30 rounded text-xs font-medium transition-colors"
                      >
                        Use
                      </button>
                    </div>
                  </div>
                  <div className="text-sm text-blue-100">
                    <div className="mb-1">{credential?.email}</div>
                    <div className="font-mono">{credential?.password}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Security Features */}
            <div className="space-y-4">
              <h3 className="font-semibold mb-3">Security Features</h3>
              <div className="space-y-3 text-sm text-blue-100">
                <div className="flex items-center space-x-3">
                  <Shield className="h-5 w-5 text-blue-300" />
                  <span>Multi-factor authentication with SMS, email, and authenticator apps</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Clock className="h-5 w-5 text-blue-300" />
                  <span>Session timeout warnings and automatic lockout protection</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Key className="h-5 w-5 text-blue-300" />
                  <span>Secure password recovery with multiple verification methods</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Activity className="h-5 w-5 text-blue-300" />
                  <span>Real-time security monitoring and activity logging</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Bell className="h-5 w-5 text-blue-300" />
                  <span>System notifications and maintenance alerts</span>
                </div>
              </div>
            </div>

            {/* Access Levels */}
            <div className="mt-6 p-4 bg-white/10 rounded-lg border border-white/20">
              <h3 className="font-semibold mb-2">Access Levels</h3>
              <ul className="text-sm text-blue-100 space-y-1">
                <li>• <strong>Admin:</strong> Full system access + user management</li>
                <li>• <strong>Doctor:</strong> Patient records, consultations, prescriptions</li>
                <li>• <strong>Nurse:</strong> Patient care, medication administration</li>
                <li>• <strong>Lab Tech:</strong> Laboratory tests, results management</li>
                <li>• <strong>Pharmacist:</strong> Pharmacy operations, prescriptions</li>
                <li>• <strong>Accountant:</strong> Finance & billing</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      {/* Modals */}
      {showPasswordRecovery && (
        <PasswordRecoveryModal
          isOpen={showPasswordRecovery}
          onClose={() => setShowPasswordRecovery(false)}
        />
      )}
      {showMfaSetup && (
        <MfaSetupModal
          isOpen={showMfaSetup}
          onClose={() => setShowMfaSetup(false)}
        />
      )}
    </div>
  );
};

export default AuthenticationPortal;